'use client';

import { ConfirmationResult } from 'firebase/auth';
import { createContext, useContext } from 'react';

import { User } from '@/typings/user';

import { useFirebaseAuth } from './hooks/use-firebase-auth';

type AuthContextType = {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, uid?: string) => Promise<void>;
  signUpError?: string;
  signUpLoading?: boolean;
  logout: (doNotDeleteFromLocalStorage: string[]) => void;
  resetError: () => void;
  resetPassword: (email: string) => Promise<void>;
  confirmation: ConfirmationResult | null;
  handlePhoneLogin: (phone: string) => Promise<void>;
  handlePhoneConfirmation: (code: string) => Promise<void>;
  phoneNumber: string | null;
};

const AuthContext = createContext({} as AuthContextType);

export type AuthProviderProps = {
  children: React.ReactNode;
  initialUser?: User;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, initialUser }) => {
  const { user, error, login, logout, signUp, resetPassword, loading, phoneAuth, resetError } =
    useFirebaseAuth();

  return (
    <AuthContext.Provider
      value={{
        user: user || (initialUser as User),
        login,
        signUp,
        logout,
        loading,
        isAuthenticated: !!user,
        resetPassword,
        error,
        confirmation: phoneAuth.confirmation,
        phoneNumber: phoneAuth.phoneNumber,
        handlePhoneLogin: phoneAuth.handlePhoneLogin,
        handlePhoneConfirmation: phoneAuth.handleValidateCode,
        resetError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
